// Order type constants and helpers
export const ORDER_TYPE_OPTIONS = [
  { value: "G", label: "General Order" },
  { value: "R", label: "Revised Order" },
  { value: "C", label: "Cancel Order" },
];

const ORDER_TYPE_MAP = {
  G: "General",
  R: "Revised",
  C: "Cancel",
};

export const getOrderTypeLabel = (code) => ORDER_TYPE_MAP[code] || code || "-";

export const getOrderTypeOption = (code) =>
  ORDER_TYPE_OPTIONS.find((o) => o.value === code) || ORDER_TYPE_OPTIONS[0];

/**
 * Derive the internal form type from formtype query param and account type.
 * PHP logic: formtype=G + agttp=0 → "AS", formtype=P + agttp=1 → "TP", formtype=S + agttp=1 → "TS"
 * @param {number} agttp - Account type (0=agent, 1=trader)
 * @param {string} formtype - URL formtype param (G/P/S). Optional, defaults based on agttp.
 * @returns {string} "AS" | "TP" | "TS"
 */
export const getFormType = (agttp, formtype) => {
  if (formtype === "S" && agttp === 1) return "TS";
  if (formtype === "P" && agttp === 1) return "TP";
  if (formtype === "G" && agttp === 0) return "AS";
  // Defaults when no formtype is provided
  if (agttp === 0) return "AS";
  return "TP";
};

/**
 * Get the ordtyp value to send to backend based on formtype query param.
 * G → "G", P → "P", S → "S". Falls back to "G" for agents.
 */
export const getOrdTypFromFormType = (agttp, formtype) => {
  if (formtype) return formtype;
  return agttp === 0 ? "G" : "P";
};

// Order transformation helpers for standardized order API

/**
 * Transform column order data to standardized order format
 * @param {Object} orderHeader - Order header information
 * @param {Array} orderItems - Array of order items with quantities
 * @param {Array} lots - Array of lot information (optional)
 * @returns {Object} Standardized order data
 */
export const transformColumnOrderToStandard = (orderHeader, orderItems, lots = []) => {
  const {
    vendorId,
    customerId,
    vendorGroupId,
    customerGroupId,
    transportId,
    despatchInfo,
    plannedDate,
    orderUom,
    remark1,
    remark2,
    orderedBy,
    billNo,
    uploadedFiles = []
  } = orderHeader;

  // Transform items with quantities to standard format
  const transformedItems = orderItems.map(row => {
    // Calculate total quantity for this row
    const totalQuantity = row.quantities.reduce((sum, q) => sum + (parseFloat(q.qty) || 0), 0);
    
    return {
      itemId: row.item?.value,
      caseId: row.caseId || 1,
      totalQuantity,
      quantities: row.quantities, // Keep the detailed quantities for column orders
      remark: "",
      itemDetails: {
        itemName: row.item?.label,
        itemData: row.item
      }
    };
  });

  return {
    orderType: "G", // General order type  
    orderSubType: "COLUMN",
    vendorId,
    customerId,
    vendorGroupId,
    customerGroupId,
    transportId: transportId || "",
    despatchInfo: despatchInfo || "",
    plannedDate,
    orderUom: orderUom || "PCS",
    remark1: remark1 || "",
    remark2: remark2 || "",
    orderedBy: orderedBy || "",
    billNo: billNo || "",
    items: transformedItems,
    lots: lots || [],
    uploadedFiles
  };
};

/**
 * Transform rate order data to standardized order format
 * @param {Object} orderHeader - Order header information
 * @param {Array} orderItems - Array of rate order items
 * @returns {Object} Standardized order data
 */
export const transformRateOrderToStandard = (orderHeader, orderItems) => {
  const {
    vendorId,
    customerId,
    vendorGroupId,
    customerGroupId,
    transportId,
    despatchInfo,
    plannedDate,
    orderUom,
    remark1,
    remark2,
    orderedBy,
    billNo,
    uploadedFiles = []
  } = orderHeader;

  // Transform rate order items to standard format
  const transformedItems = orderItems.map(item => ({
    itemId: item.itemId,
    caseId: item.caseId || 1,
    quantity: item.quantity,
    totalQuantity: item.quantity, // For rate orders, quantity = totalQuantity
    rate: item.rate || 0,
    remark: item.remark || "",
    itemDetails: item.itemDetails
  }));

  return {
    orderType: "G", // General order type
    orderSubType: "RATE",
    vendorId,
    customerId,
    vendorGroupId,
    customerGroupId,
    transportId: transportId || "",
    despatchInfo: despatchInfo || "",
    plannedDate,
    orderUom: orderUom || "PCS",
    remark1: remark1 || "",
    remark2: remark2 || "",
    orderedBy: orderedBy || "",
    billNo: billNo || "",
    items: transformedItems,
    lots: [], // Rate orders don't have lots
    uploadedFiles
  };
};

/**
 * Validate order data before submission
 * @param {Object} orderData - Standardized order data
 * @returns {Object} Validation result with isValid flag and errors array
 */
export const validateOrderData = (orderData) => {
  const errors = [];

  // Required field validations
  if (!orderData.vendorId) {
    errors.push("Vendor is required");
  }

  if (!orderData.customerId) {
    errors.push("Customer is required");
  }

  if (!orderData.items || orderData.items.length === 0) {
    errors.push("At least one item is required");
  }

  // Item validations
  if (orderData.items) {
    orderData.items.forEach((item, index) => {
      if (!item.itemId) {
        errors.push(`Item ${index + 1}: Item selection is required`);
      }

      if (orderData.orderSubType === "RATE") {
        if (!item.quantity || parseFloat(item.quantity) <= 0) {
          errors.push(`Item ${index + 1}: Quantity must be greater than 0`);
        }
      } else if (orderData.orderSubType === "COLUMN") {
        if (!item.totalQuantity || parseFloat(item.totalQuantity) <= 0) {
          errors.push(`Item ${index + 1}: Total quantity must be greater than 0`);
        }
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Calculate order totals from items
 * @param {Array} items - Array of order items
 * @param {String} orderSubType - Order sub type (RATE or COLUMN)
 * @returns {Object} Order totals
 */
export const calculateOrderTotals = (items, orderSubType) => {
  let totalQuantity = 0;
  let totalAmount = 0;
  let totalItems = items.length;

  items.forEach(item => {
    if (orderSubType === "RATE") {
      const quantity = parseFloat(item.quantity) || 0;
      const rate = parseFloat(item.rate) || 0;
      totalQuantity += quantity;
      totalAmount += quantity * rate;
    } else if (orderSubType === "COLUMN") {
      totalQuantity += parseFloat(item.totalQuantity) || 0;
      // For column orders, we might not have rates per item
    }
  });

  return {
    totalItems,
    totalQuantity,
    totalAmount
  };
}; 